import {
    closestCenter,
    CollisionDetection,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    pointerWithin,
    rectIntersection,
    UniqueIdentifier,
} from '@dnd-kit/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import {
    DemeterDashboardModel,
    DemeterDashboardWidgetLayoutType,
    DemeterDashboardWidgetModel,
    DemeterDashboardWidgetPageElementModel,
    DemeterPageElementModel,
    UpsertDemeterDashboardRequest,
} from '../../../Generated/Raven-Demeter';
import { ComponentHeaderRightSideOptions } from '../../Components/Form/Inputs/ComponentMoreOptionsHorizontalDots';
import TextInput from '../../Components/Form/Inputs/TextInput';
import ActionModal from '../../Components/Modals/ActionModal/ActionModal';
import ConfirmModal from '../../Components/Modals/ConfirmModal/ConfirmModal';
import PageElementModal from '../../Components/Modals/PageElementModal/PageElementModal';
import useLanguage from '../../Services/Language/useLanguageHook';
import { pageElementsConstraintsAndInitializations } from './DashboardCustomizationDefinitions';
import dashboardCustomizationService, { IDragAndDropRow } from './DashboardCustomizationService';
import DashboardWidgetContainer from './DashboardWidgetContainer';
import styles from './DragAndDropWrapper.module.scss';
import DraggableItem from './DraggableItem';
import RowDividerDropContainer from './RowDividerDropContainer';

interface IDashboardContainerProps {
    dashboard: DemeterDashboardModel;
    pageElements: DemeterPageElementModel[];
    isInEditMode: boolean;
    updateUpsertDashboardRequestFromContainer: () => void;
    setUpsertDemeterDashboardRequest: (upsertDashboard: UpsertDemeterDashboardRequest) => void;
}

const parseIntRadix = 10;

const DashboardContainer: React.FC<IDashboardContainerProps> = (props: IDashboardContainerProps) => {
    const [translations] = useLanguage();
    const [dragAndDropRows, setDragAndDropRows] = useState<IDragAndDropRow[]>([]);
    const [activeItemId, setActiveItemId] = useState<UniqueIdentifier | null>(null);

    const [openSelector, setOpenSelector] = useState(false);
    const [openPageElementModalRowOrder, setOpenPageElementModalRowOrder] = useState<number>(0);
    const [openPageElementModalWidgetOrder, setOpenPageElementModalWidgetOrder] = useState<number>(0);

    const [openConfirmDeleteWidgetModal, setOpenConfirmDeleteWidgetModal] = useState(false);
    const [openRenameWidgetModal, setOpenRenameWidgetModal] = useState(false);
    const [widgetName, setWidgetName] = useState<string>();

    const [updateDemeterDashboardWidgetRequest, setUpdateDemeterDashboardWidgetRequest] = useState<DemeterDashboardWidgetPageElementModel>();
    const demeterDashboard = useRef<DemeterDashboardModel>(props.dashboard);
    const dashboardChangesMade = useRef<number>(0);

    const customCollisionDetection: CollisionDetection = (collisionArguments) => {
        const pointerIntersections = pointerWithin(collisionArguments);
        const rowDividerCollisions = pointerIntersections.filter((collision) => collision.id.toString().startsWith('row-divider-'));
        if (rowDividerCollisions.length > 0) {
            return rowDividerCollisions;
        }

        const rectCollisions = rectIntersection(collisionArguments);
        return rectCollisions.length > 0 ? rectCollisions : closestCenter(collisionArguments);
    };

    const addTemplatePageElement = (selectedPageElement: DemeterPageElementModel) => {
        const { pageElementType } = selectedPageElement;
        dashboardChangesMade.current += 1;
        if (!pageElementType) {
            return;
        }

        const rowHasEnforcedLayoutType = pageElementsConstraintsAndInitializations.find((x) => x.pageElementType === pageElementType)?.layoutType;
        const updateDashboardRows = [...(demeterDashboard.current.rows || [])];
        const forceCreateNewRow =
            rowHasEnforcedLayoutType === DemeterDashboardWidgetLayoutType.FullWidth && updateDashboardRows[openPageElementModalRowOrder]?.rows?.length > 0;

        const newRowOrder = forceCreateNewRow ? openPageElementModalRowOrder + 1 : openPageElementModalRowOrder;
        const newWidgetOrder = forceCreateNewRow ? 0 : openPageElementModalWidgetOrder;

        const newDemeterDashboardWidgetPageElementModel: DemeterDashboardWidgetPageElementModel = {
            demeterDashboardWidgetPageElementGuid: `${dashboardChangesMade.current}`,
            order: newWidgetOrder,
            options: {},
            pageElement: selectedPageElement,
        };

        const newDemeterDashboardWidgetModel: DemeterDashboardWidgetModel = {
            name: '',
            order: newRowOrder,
            layoutType: rowHasEnforcedLayoutType,
            width: 0,
            height: 0,
            positionX: 0,
            positionY: 0,
            options: {},
            rows:
                !updateDashboardRows[newRowOrder] || forceCreateNewRow
                    ? [newDemeterDashboardWidgetPageElementModel]
                    : [...updateDashboardRows[newRowOrder].rows, newDemeterDashboardWidgetPageElementModel],
        };

        if (!forceCreateNewRow) {
            updateDashboardRows[openPageElementModalRowOrder] = newDemeterDashboardWidgetModel;
        } else {
            updateDashboardRows.splice(openPageElementModalRowOrder, 0, newDemeterDashboardWidgetModel);
        }

        const newDemeterDashboardModel: DemeterDashboardModel = {
            ...demeterDashboard.current,
            rows: [...updateDashboardRows].map((row, index) => ({ ...row, order: index })).sort((a, b) => a.order - b.order),
        };

        refreshContainerStateInEditView(newDemeterDashboardModel);
    };

    const handleAddWidgetClick = (rowOrder: number, widgetOrder: number) => {
        setOpenPageElementModalRowOrder(rowOrder);
        setOpenPageElementModalWidgetOrder(widgetOrder);
        setOpenSelector(true);
    };

    const confirmDeleteWidget = () => {
        if (!updateDemeterDashboardWidgetRequest) {
            return;
        }

        const dashboardCopy: DemeterDashboardModel = dashboardCustomizationService.createDashboardDeepCopy(demeterDashboard.current);
        dashboardChangesMade.current += 1;
        const updatedDashboard = dashboardCustomizationService.deleteWidget(dashboardCopy, updateDemeterDashboardWidgetRequest);
        refreshContainerStateInEditView(updatedDashboard);
    };

    const handleUpdateWidgetName = () => {
        if (!updateDemeterDashboardWidgetRequest || !widgetName) {
            return;
        }

        const dashboardCopy: DemeterDashboardModel = dashboardCustomizationService.createDashboardDeepCopy(demeterDashboard.current);
        dashboardChangesMade.current += 1;
        const updatedDashboard: DemeterDashboardModel = dashboardCustomizationService.renameWidget(
            dashboardCopy,
            updateDemeterDashboardWidgetRequest,
            widgetName,
        );

        refreshContainerStateInEditView(updatedDashboard);
    };

    const updateUpsertDashboardRequest = (dashboard: DemeterDashboardModel) => {
        const upsertDashboardRequest: UpsertDemeterDashboardRequest = {
            ...dashboard,
            rows: dashboard.rows.map((row) => ({
                ...row,
                rows: row.rows.map((widget) => {
                    if (
                        !Number.isNaN(Number(widget.demeterDashboardWidgetPageElementGuid)) &&
                        Number(widget.demeterDashboardWidgetPageElementGuid) <= dashboardChangesMade.current
                    ) {
                        return {
                            ...widget,
                            demeterDashboardWidgetPageElementGuid: applicationConstants.ZeroGuid,
                        };
                    }

                    return widget;
                }),
            })),
        };

        props.setUpsertDemeterDashboardRequest(upsertDashboardRequest);
    };

    const refreshContainerStateInEditView = (dashboard: DemeterDashboardModel) => {
        demeterDashboard.current = dashboard;
        setUpdateDemeterDashboardWidgetRequest(undefined);
        setOpenRenameWidgetModal(false);
        setWidgetName(undefined);
        setOpenConfirmDeleteWidgetModal(false);
        setOpenSelector(false);

        const updatedDragAndDropRows = dashboardCustomizationService.getDashboardDragAndDropRowsWithAddWidget(
            dashboardCustomizationService.getDashboardDragAndDropRows(dashboard.rows, true, props.pageElements, handleMoreOptionsSelection, dragAndDropRows),
            handleAddWidgetClick,
        );

        setDragAndDropRows(updatedDragAndDropRows);

        if (dashboardChangesMade.current > 0) {
            updateUpsertDashboardRequest(dashboard);
        }
    };

    const handleMoreOptionsSelection = (value: ComponentHeaderRightSideOptions, dashboardWidget: DemeterDashboardWidgetPageElementModel) => {
        if (value === ComponentHeaderRightSideOptions.RenameWidget) {
            setUpdateDemeterDashboardWidgetRequest(dashboardWidget);
            setOpenRenameWidgetModal(true);
            return;
        }

        if (value === ComponentHeaderRightSideOptions.DeleteWidget) {
            setUpdateDemeterDashboardWidgetRequest(dashboardWidget);
            setOpenConfirmDeleteWidgetModal(true);
            return;
        }

        const dashboardCopy: DemeterDashboardModel = dashboardCustomizationService.createDashboardDeepCopy(demeterDashboard.current);

        if (value === ComponentHeaderRightSideOptions.CloneWidget) {
            dashboardChangesMade.current += 1;
            const updateDashboard = dashboardCustomizationService.cloneWidget(dashboardCopy, dashboardWidget, dashboardChangesMade.current);
            if (!updateDashboard) {
                return;
            }

            refreshContainerStateInEditView(updateDashboard);
            return;
        }

        if (value === ComponentHeaderRightSideOptions.Maximize) {
            dashboardChangesMade.current += 1;
            const updatedDashboard = dashboardCustomizationService.maximizeWidget(dashboardCopy, dashboardWidget);
            refreshContainerStateInEditView(updatedDashboard);
            return;
        }

        if (value === ComponentHeaderRightSideOptions.Minimize) {
            dashboardChangesMade.current += 1;
            const updatedDashboard = dashboardCustomizationService.minimizeWidget(dashboardCopy, dashboardWidget);
            refreshContainerStateInEditView(updatedDashboard);
        }
    };

    const handleOnDragStart = (event: DragStartEvent) => {
        setActiveItemId(event.active.id);
    };

    const handleOnDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        let updatedRows: IDragAndDropRow[] | null;
        if (!over?.id || active.id.toString() === over.id.toString()) {
            setActiveItemId(null);
            return;
        }

        if (over.id.toString().startsWith('row-divider-')) {
            const targetRowIndex = parseInt(over.id.toString().replace('row-divider-', ''), parseIntRadix);
            updatedRows = dashboardCustomizationService.handleWidgetDropToNewRow(dragAndDropRows, active.id, targetRowIndex, handleAddWidgetClick);
        } else {
            updatedRows = dashboardCustomizationService.handleWidgetDrop(dragAndDropRows, active.id, over.id);
        }

        if (!updatedRows) {
            setActiveItemId(null);
            return;
        }

        const updatedDashboard = dashboardCustomizationService.convertDragAndDropRowsToDashboardModel(updatedRows, demeterDashboard.current);
        const updatedDragAndDropRows = dashboardCustomizationService.getDashboardDragAndDropRowsWithAddWidget(
            dashboardCustomizationService.getDashboardDragAndDropRows(updatedDashboard.rows, true, props.pageElements, handleMoreOptionsSelection, updatedRows),
            handleAddWidgetClick,
        );

        setDragAndDropRows(updatedDragAndDropRows);
        dashboardChangesMade.current += 1;
        demeterDashboard.current = updatedDashboard;
        updateUpsertDashboardRequest(updatedDashboard);
        setActiveItemId(null);
    };

    const activeComponent = useMemo(() => {
        if (!activeItemId) {
            return null;
        }

        return dragAndDropRows.find((x) => x.widgets.some((y) => y.id === activeItemId))?.widgets.find((x) => x.id === activeItemId)?.component;
    }, [activeItemId, dragAndDropRows]);

    useEffect(() => {
        if (!props.isInEditMode) {
            setDragAndDropRows(
                dashboardCustomizationService.getDashboardDragAndDropRows(props.dashboard.rows, false, props.pageElements, handleMoreOptionsSelection),
            );
            return;
        }

        refreshContainerStateInEditView(props.dashboard);
    }, [props.isInEditMode, props.dashboard]);

    useEffect(
        () => () => {
            if (dashboardChangesMade.current > 0) {
                props.updateUpsertDashboardRequestFromContainer();
                dashboardChangesMade.current = 0;
            }
        },
        [props.isInEditMode, props.dashboard],
    );

    return (
        <div className={styles.dragAndDrop_context_container}>
            <DndContext
                collisionDetection={customCollisionDetection}
                onDragStart={(event) => handleOnDragStart(event)}
                onDragEnd={(event) => handleOnDragEnd(event)}
            >
                {dragAndDropRows.map((row, index) => (
                    <React.Fragment key={row.stableRowId}>
                        <RowDividerDropContainer rowIndex={index} />
                        <DashboardWidgetContainer layoutType={row.layoutType} widgets={row.widgets} />
                    </React.Fragment>
                ))}
                {createPortal(
                    <DragOverlay>{activeItemId && <DraggableItem id={activeItemId.toString()}>{activeComponent}</DraggableItem>}</DragOverlay>,
                    document.body,
                )}
            </DndContext>
            <ConfirmModal
                header={translations.dashboardCustomization.widget.deleteWidget}
                message={translations.dashboardCustomization.messages.confirmDeleteWidget}
                showModal={openConfirmDeleteWidgetModal}
                handleConfirm={() => confirmDeleteWidget()}
                handleCancel={() => {
                    setOpenConfirmDeleteWidgetModal(false);
                    setUpdateDemeterDashboardWidgetRequest(undefined);
                }}
                track={{ eventCategory: 'Dashboard', eventAction: 'DeleteWidget' }}
            />
            <ActionModal
                header={translations.dashboardCustomization.widget.renameWidget}
                showModal={openRenameWidgetModal}
                actionButtonName={translations.actions.save}
                secondaryActionButtonName={translations.actions.cancel}
                handleAction={() => handleUpdateWidgetName()}
                handleCancel={() => {
                    setOpenRenameWidgetModal(false);
                    setUpdateDemeterDashboardWidgetRequest(undefined);
                }}
                track={{ eventCategory: 'Dashboard', eventAction: 'RenameWidget' }}
            >
                <TextInput
                    value={widgetName}
                    placeholder={updateDemeterDashboardWidgetRequest?.options?.customTitle}
                    handleTextChange={setWidgetName}
                    required
                    track={{ eventCategory: 'Dashboard', eventAction: 'WidgetNameChanged', eventValue: widgetName }}
                />
            </ActionModal>
            <PageElementModal
                title={translations.dashboardCustomization.titles.addWidget}
                pageElements={props.pageElements}
                open={openSelector}
                handleClose={() => setOpenSelector(false)}
                handleAdd={addTemplatePageElement}
                track={{ eventCategory: 'Dashboard', eventAction: 'AddWidget' }}
            />
        </div>
    );
};

export default DashboardContainer;
